/**
 * Canvas Background Store — toggle + selection state for the dynamic
 * background image / video that lives behind ReactFlow nodes.
 *
 * Persisted to localStorage so the user's pick survives reloads.
 * First-time users get a default looping video background so the canvas
 * never feels empty on first contact.
 */
import { create } from 'zustand';

const STORAGE_KEY = 'medialabs.canvas-bg.v1';

// Default background applied to every brand-new user (or anyone who has
// never picked a custom background). 720p H.264, ~1.4 MB, 15s loop.
const DEFAULT_BG = {
  enabled: true,
  url: 'https://pub-063ce05a26c2404fa8909ca12197a910.r2.dev/medialabs_defaults/canvas_bg_default_web.mp4',
  kind: 'video',
  label: 'MediaLabs Default',
  opacity: 0.55,
  blur: 0,
};

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persist = (state) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        enabled: state.enabled,
        url: state.url,
        kind: state.kind,
        opacity: state.opacity,
        blur: state.blur,
        label: state.label,
      })
    );
  } catch { /* ignore quota errors */ }
};

const persisted = loadInitial();
// First-time users (no saved state) → seed with the default video bg.
const initial = persisted ?? DEFAULT_BG;

// Fondo fijado por el admin para toda la plataforma. Llega async desde
// GET /api/platform/background y reemplaza a DEFAULT_BG como fallback.
let platformDefault = null;

const useCanvasBgStore = create((set, get) => ({
  enabled: initial.enabled ?? false,
  url:     initial.url     ?? null,
  kind:    initial.kind    ?? null,   // 'image' | 'video' | null
  label:   initial.label   ?? null,
  opacity: initial.opacity ?? 0.55,   // 0..1 — how strong the bg shows
  blur:    initial.blur    ?? 0,      // 0..20 px

  // Llamado una vez al montar el canvas con la config del admin.
  // Impone el fondo de plataforma SOLO si el usuario nunca eligió uno propio.
  applyPlatformDefault: (d) => {
    platformDefault = (d && d.url)
      ? {
          enabled: true,
          url: d.url,
          kind: d.kind || 'image',
          label: d.label || 'Fondo MediaLabs',
          opacity: DEFAULT_BG.opacity,
          blur: DEFAULT_BG.blur,
        }
      : null;
    if (!platformDefault || loadInitial()) return;
    set({ ...platformDefault });
    persist(get());
  },

  setEnabled: (v) => {
    set({ enabled: !!v });
    persist(get());
  },

  setBackground: ({ url, kind, label }) => {
    set({ url: url || null, kind: kind || null, label: label || null });
    if (url && !get().enabled) set({ enabled: true });
    persist(get());
  },

  resetToDefault: () => {
    const d = platformDefault || DEFAULT_BG;
    set({
      enabled: d.enabled,
      url: d.url,
      kind: d.kind,
      label: d.label,
      opacity: d.opacity,
      blur: d.blur,
    });
    persist(get());
  },

  clearBackground: () => {
    set({ url: null, kind: null, label: null });
    persist(get());
  },

  setOpacity: (v) => {
    const clamped = Math.max(0, Math.min(1, v));
    set({ opacity: clamped });
    persist(get());
  },

  setBlur: (v) => {
    const clamped = Math.max(0, Math.min(20, v));
    set({ blur: clamped });
    persist(get());
  },
}));

export default useCanvasBgStore;
